import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { Download, Search, TableView } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PageHeaderLayout from '../../../components/Layout/PageHeaderLayout';
import { ReportServices } from '../ReportServices';
import WeeklyReportDocument, {
  bookingStatsHierarchyMetrics,
  weeklySummaryMetrics,
} from './WeeklyReportDocument';
import {
  buildServiceCityMatrix,
  capitalizeReportLabel,
  isReportGeographyVisible,
} from './weeklyReportUtils';

const getLastWeekRange = () => {
  const today = dayjs();
  const thisMonday = today.subtract((today.day() + 6) % 7, 'day');
  return {
    startDate: thisMonday.subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: thisMonday.subtract(1, 'day').format('YYYY-MM-DD'),
  };
};

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const WeeklyOperationsReport = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const reportRef = useRef(null);

  const { control, handleSubmit, getValues, setValue, formState: { errors } } = useForm({
    defaultValues: getLastWeekRange(),
  });

  useEffect(() => {
    dispatch({ type: 'menuSelect', payload: 'weekly-operations-report' });
  }, [dispatch]);

  const fetchReport = useCallback(async (values) => {
    setLoading(true);
    setError('');
    try {
      const response = await ReportServices.fetchWeeklyOperationsReport(values);
      if (response?.success === 1) {
        setReport(response.data);
      } else {
        setReport(null);
        setError(response?.message || 'Unable to load the weekly report.');
      }
    } catch (requestError) {
      setReport(null);
      setError(requestError?.message || 'Unable to load the weekly report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(getValues());
  }, [fetchReport, getValues]);

  const periodLabel = useMemo(() => {
    if (!report?.period) return '';
    return `${dayjs(report.period.startDate).format('DD MMM YYYY')} – ${dayjs(report.period.endDate).format('DD MMM YYYY')}`;
  }, [report]);

  const downloadCsv = () => {
    if (!report) return;

    const summary = report.summary || {};
    const categoryGeographyTotals = report.categoryGeographyTotals || summary;
    const topPerformingExperts = (report.topPerformingExperts || [])
      .slice(0, 10)
      .map((row, index) => ({ ...row, rank: index + 1 }));
    const positiveCategoryEarnings = (report.earningsByCategory || [])
      .filter((row) => Number(row.weeklyEarnings) > 0);
    const positiveGeographyEarnings = (report.earningsByGeography || [])
      .filter((row) => (
        Number(row.weeklyEarnings) > 0 && isReportGeographyVisible(row.geography)
      ));
    const serviceCityMatrix = buildServiceCityMatrix(report.servicesByCity || []);
    const rows = [
      ['CuDel Weekly Operations Report'],
      ['Period', periodLabel],
      ['Timezone', report.period?.timezone || 'Asia/Kolkata'],
      [],
      ['Summary'],
      ['Metric', 'Count'],
      ...weeklySummaryMetrics.map(([key, label]) => [label, summary[key] || 0]),
      [],
      ['Booking Stats'],
      ['Metric', 'Count'],
      ...bookingStatsHierarchyMetrics.map(([key, label]) => [label, summary[key] || 0]),
      [],
      ['Category & Geography Wise Expert Count'],
      ['Category', 'Region', 'Total Experts', 'Active Experts', 'Completed Bookings', 'Cancelled Bookings'],
      ...(report.categoryGeography || []).filter((row) => (
        isReportGeographyVisible(row.geography)
      )).map((row) => [
        capitalizeReportLabel(row.category, 'Uncategorized'),
        row.geography,
        row.totalExperts,
        row.activeExperts,
        row.executedBookings,
        row.missedBookings,
      ]),
      [
        'Overall Total',
        '',
        categoryGeographyTotals.totalExpertCount || 0,
        categoryGeographyTotals.activeExpertCount || 0,
        categoryGeographyTotals.completedBookings || 0,
        categoryGeographyTotals.missedBookings || 0,
      ],
      [],
      ['Top 10 Weekly Performers'],
      ['Rank', 'Expert', 'Category', 'Geography', 'Executed', 'Cancelled', 'Weekly Earnings', 'Payout Eligibility'],
      ...topPerformingExperts.map((row) => [
        row.rank,
        row.expertName,
        row.category,
        row.geography,
        row.executedBookings,
        row.missedBookings,
        row.weeklyEarnings,
        row.payoutEligibility || 'Unavailable',
      ]),
      [],
      ['Earnings by Category'],
      ['Category', 'Weekly Earnings'],
      ...positiveCategoryEarnings.map((row) => [row.category, row.weeklyEarnings]),
      [],
      ['Earnings by Geography'],
      ['Geography', 'Weekly Earnings'],
      ...positiveGeographyEarnings.map((row) => [row.geography, row.weeklyEarnings]),
      [],
      ['Top 20 Expert-Registered Services by City'],
      ['Category', 'Subcategory', 'Service', ...serviceCityMatrix.cities],
      ...serviceCityMatrix.rows.map((row) => [
        row.category,
        row.subcategory,
        row.service,
        ...serviceCityMatrix.cities.map((_, index) => row[`city_${index}`]),
      ]),
      [
        'Total',
        '',
        '',
        ...serviceCityMatrix.cities.map((_, index) => (
          serviceCityMatrix.rows.reduce((sum, row) => sum + Number(row[`city_${index}`] || 0), 0)
        )),
      ],
      ['Total Services Added', summary.expertServicesAdded || 0],
      [],
      ['Bookings by Region'],
      ['Region', 'Total Bookings', 'Completed', 'Admin Panel', 'App / Customer'],
      ...(report.bookingsByCity || []).map((row) => [
        row.city,
        row.totalBookings,
        row.completedBookings,
        row.adminPanelBookings,
        row.customerAppBookings,
      ]),
    ];

    const csv = `\uFEFF${rows.map((row) => row.map(csvEscape).join(',')).join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `cudel-weekly-report-${report.period.startDate}-to-${report.period.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    if (!report) return;

    setDownloading(true);
    setError('');
    try {
      const pdf = await ReportServices.downloadWeeklyOperationsReportPdf(report.period);
      const url = URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cudel-weekly-report-${report.period.startDate}-to-${report.period.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(downloadError?.message || 'Unable to download the PDF report.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
      <PageHeaderLayout headerTitle="CuDel Weekly Operations Report">
        <Button
          variant="outlined"
          size="small"
          startIcon={<TableView />}
          disabled={!report || loading || downloading}
          onClick={downloadCsv}
        >
          Export CSV
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
          disabled={!report || loading || downloading}
          onClick={downloadPdf}
        >
          {downloading ? 'Preparing PDF' : 'Download Report'}
        </Button>
      </PageHeaderLayout>

      <Paper
        component="form"
        onSubmit={handleSubmit(fetchReport)}
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={5} md={3}>
            <Controller
              name="startDate"
              control={control}
              rules={{ required: 'Start date is required' }}
              render={({ field }) => (
                <DatePicker
                  label="Start date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => {
                    const startDate = value?.isValid() ? value.format('YYYY-MM-DD') : '';
                    field.onChange(startDate);
                    setValue(
                      'endDate',
                      startDate ? value.add(6, 'day').format('YYYY-MM-DD') : '',
                      { shouldDirty: true, shouldValidate: true },
                    );
                  }}
                  slotProps={{
                    textField: {
                      name: field.name,
                      inputRef: field.ref,
                      onBlur: field.onBlur,
                      size: 'small',
                      fullWidth: true,
                      error: Boolean(errors.startDate),
                      helperText: errors.startDate?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={3}>
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: 'End date is required',
                validate: (value) => value >= getValues('startDate') || 'End date must be after start date',
              }}
              render={({ field }) => (
                <DatePicker
                  label="End date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      name: field.name,
                      inputRef: field.ref,
                      onBlur: field.onBlur,
                      size: 'small',
                      fullWidth: true,
                      error: Boolean(errors.endDate),
                      helperText: errors.endDate?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={2} md="auto">
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              disabled={loading}
              fullWidth
            >
              Generate
            </Button>
          </Grid>
          {periodLabel && (
            <Grid item xs={12} md>
              <Typography color="text.secondary" sx={{ textAlign: { xs: 'left', md: 'right' }, fontSize: '.8rem' }}>
                Reporting period: {periodLabel} (IST)
              </Typography>
            </Grid>
          )}
        </Grid>
        </LocalizationProvider>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {report && (
        <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden', pb: 1.5 }}>
          <WeeklyReportDocument ref={reportRef} report={report} />
        </Box>
      )}

    </Box>
  );
};

export default WeeklyOperationsReport;
